/**
 * QuickAddFooter — the save bar at the bottom of the QuickAdd
 * modal. Hosts the savedToast status hint, the keyboard cheat-
 * sheet line, and the primary/secondary action buttons.
 *
 * In brain-dump mode the secondary "Save & add another" button
 * is hidden (the primary "Capture" button already chains) and
 * the cheat-sheet line switches to "Enter to capture · Esc when
 * you're done".
 */
import { Check, Loader2 } from 'lucide-react';

export function QuickAddFooter({
  brainDump,
  saving,
  canSave,
  savedToast,
  onSave,
  onSaveAndAddAnother,
}) {
  const disabled = !canSave || saving;
  const disabledStyle = {
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  return (
    <div
      className="px-5 py-3 flex items-center justify-between gap-2"
      style={{ background: 'var(--pd-panel)', borderTop: '1px solid var(--pd-border)' }}
    >
      <div className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
        {savedToast ? (
          <span
            data-testid="quickadd-saved-toast"
            className="flex items-center gap-1.5"
            style={{ color: '#10b981' }}
          >
            <Check className="w-3 h-3" />
            Saved as {savedToast.label}
            {brainDump ? <span style={{ opacity: 0.6 }}>· ready for the next one</span> : null}
          </span>
        ) : brainDump ? (
          <>Enter to capture & continue · Esc when you&apos;re done</>
        ) : (
          <>Cmd+Enter to save · Shift+Cmd+Enter save & add another</>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {!brainDump ? (
          <button
            type="button"
            data-testid="quickadd-save-and-another"
            onClick={onSaveAndAddAnother}
            disabled={disabled}
            className="px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
            style={{
              background: 'var(--pd-panel-2)',
              color: 'var(--pd-text)',
              border: '1px solid var(--pd-border)',
              ...disabledStyle,
            }}
          >
            Save & add another
          </button>
        ) : null}
        <button
          type="button"
          data-testid="quickadd-save"
          onClick={onSave}
          disabled={disabled}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
          style={{
            // In Brain Dump mode the Capture button picks up the
            // brand amber, matching the modal halo + toggle pill +
            // captured-count number — one consistent "warm =
            // creative-flow" colour cue across the surface. In
            // normal Quick Add mode it stays on the monochrome
            // accent so the CTA reads as a quiet, system-level
            // primary action.
            background: brainDump ? 'var(--pd-brand)' : 'var(--pd-accent)',
            color: brainDump ? '#1A1300' : 'var(--pd-canvas)',
            border: '1px solid ' + (brainDump ? 'var(--pd-brand)' : 'var(--pd-accent)'),
            ...disabledStyle,
          }}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {saving ? 'Saving…' : (brainDump ? 'Capture' : 'Save')}
        </button>
      </div>
    </div>
  );
}
